/**
 * 告警规则与事件模块（OFBiz 新接口）
 * ------------------------------------------------------------------
 * 对应后端：POST /itoss/control/json/alarmRule/* 与 /alarmEvent/*
 *
 * 迁移自前端原接口（odata_client.js / alarm_action.js / odatayft.js）：
 *   告警规则：getAlarmRuleList, createAlarmRule, updateAlarmRule,
 *            deleteAlarmRule, startOrStopAlarmRule
 *   告警事件：getAlarmEvent, getAlarmEventLogs, updateAlarmEventStatus,
 *            startOrStopAlarmEvent
 *
 * 说明：原前端 getAlarmEvent 同时存在 OData(/bods.svc/GetAlarmEvents) 和
 *      newsam(/newsam/sam/api/v1/alarm/getAlarmEvents) 两套调用，新接口统一为
 *      /alarmEvent/list。
 */
import { ofbiz } from './http';

/* ===================== 告警规则 ===================== */

/**
 * 告警规则列表（分页，替代旧 getAlarmRuleList）
 * @param {object} [query] { currentStatusId, monitorId, alarmLevel, workEffortName, keyword }
 * @param {number} [page=1]
 * @param {number} [pageSize=20]
 * @returns {Promise<{list, total, page, pageSize}>}
 */
export function getAlarmRuleList(query = {}, page = 1, pageSize = 20) {
  return ofbiz.postPage('alarmRule/list', query, page, pageSize);
}

/**
 * 规则详情
 * @param {string} ruleId
 * @returns {Promise<object>}
 */
export function getAlarmRuleById(ruleId) {
  return ofbiz.post('alarmRule/getById', null, { ruleId });
}

/**
 * 新增规则（替代旧 createAlarmRule）
 * 纯通知策略语义（见 doc/15）：只要求 ruleName 必填；其余为可选的通知策略字段
 * （alarmLevel/dutyGroup/emailEnabled/emailTemplate/smsEnabled/smsTemplate/
 *   wechatEnabled/wechatUser/upgradeTimes/recipients/upgradeRecipients/alarmStart...），
 * 以及关联设备 equipmentIds 数组。
 * @param {object} data { ruleName(必填), ... }
 * @returns {Promise<object>}
 */
export function createAlarmRule(data) {
  return ofbiz.post('alarmRule/create', data);
}

/**
 * 更新规则（替代旧 updateAlarmRule）
 * @param {object} data 必含 ruleId
 * @returns {Promise<object>}
 */
export function updateAlarmRule(data) {
  return ofbiz.post('alarmRule/update', data);
}

/**
 * 删除规则（替代旧 deleteAlarmRule）
 * @param {string} ruleId
 * @returns {Promise<object>}
 */
export function deleteAlarmRule(ruleId) {
  return ofbiz.post('alarmRule/delete', null, { ruleId });
}

/**
 * 启用/禁用规则（替代旧 startOrStopAlarmRule）
 * 后端为单接口 toggle，按当前状态自动切换。
 * @param {string} ruleId
 * @returns {Promise<object>}
 */
export function toggleAlarmRule(ruleId) {
  return ofbiz.post('alarmRule/toggle', null, { ruleId });
}

/* ===================== 告警事件 ===================== */

/**
 * 告警事件列表（分页，替代旧 getAlarmEvent）
 *
 * 原 getAlarmEvent 两套实现并存，新接口统一走 alarmEvent/list。
 * @param {object} [query] { currentStatusId, equipmentId, monitorId, alarmLevel, keyword }
 * @param {number} [page=1]
 * @param {number} [pageSize=20]
 * @returns {Promise<{list, total, page, pageSize}>}
 */
export function getAlarmEventList(query = {}, page = 1, pageSize = 20) {
  return ofbiz.postPage('alarmEvent/list', query, page, pageSize);
}

/**
 * 事件详情（含操作日志，替代旧 getAlarmDetails）
 * @param {string} eventId
 * @returns {Promise<object>}
 */
export function getAlarmEventById(eventId) {
  return ofbiz.post('alarmEvent/getById', null, { eventId });
}

/**
 * 确认事件（替代旧 updateAlarmEventStatus 的"确认"分支 / startOrStopAlarmEvent）
 * @param {string} eventId
 * @param {string} [confirmNote] 确认备注
 * @returns {Promise<object>}
 */
export function confirmAlarmEvent(eventId, confirmNote) {
  return ofbiz.post('alarmEvent/confirm', null, { eventId, confirmNote });
}

/**
 * 关闭事件（替代旧 updateAlarmEventStatus 的"关闭"分支 / startOrStopAlarmEvent）
 * @param {string} eventId
 * @param {string} [closeNote] 关闭备注
 * @returns {Promise<object>}
 */
export function closeAlarmEvent(eventId, closeNote) {
  return ofbiz.post('alarmEvent/close', null, { eventId, closeNote });
}

/**
 * 告警统计（大屏用）
 * @returns {Promise<object>}
 */
export function getAlarmEventStats() {
  return ofbiz.post('alarmEvent/stats');
}

/**
 * 告警事件操作日志（分页，替代旧 getAlarmEventLogs）
 * @param {string|string[]} eventIds 事件 ID，逗号分隔或单个
 * @param {object} [opts] { noteName, page, pageSize } noteName 如 CONFIRM/CLOSE
 * @returns {Promise<{list, total, page, pageSize}>}
 */
export function getAlarmEventLogs(eventIds, { noteName, page = 1, pageSize = 20 } = {}) {
  const ids = Array.isArray(eventIds) ? eventIds.join(',') : eventIds;
  return ofbiz.postPage('alarmEvent/logs', { eventIds: ids, noteName }, page, pageSize);
}

/**
 * 告警名称列表（下拉用）
 * @returns {Promise<Array>}
 */
export function getAlarmNameList() {
  return ofbiz.post('alarmEvent/alarmNameList');
}
